/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import { MouseEvent } from "react"
import { useLocation, useNavigate } from "react-router-dom"

/**
 * In-page "Back to …" links that keep the browser history straight.
 *
 * A plain link to the parent page PUSHES a history entry, so parent → child →
 * "Back to parent" leaves the stack as parent, child, parent and the browser's
 * Back button then returns to the child the user just left. Going back one
 * entry instead is only right when the entry underneath really is the parent —
 * not after opening the child from a pasted URL or a bookmark, nor when the
 * child was reached some other way.
 *
 * So the parent names itself when it opens the child (`openedFromState`), which
 * the router keeps on the child's history entry (it survives reloads and
 * back/forward). `useBackLink` then goes back one entry when the entry
 * underneath is the link's target, and otherwise leaves the link to navigate
 * normally.
 */
type OpenedFromState = { openedFrom?: string }

/** Navigation state for opening a child page from the page at `pathname`. */
const openedFromState = (pathname: string): OpenedFromState => ({ openedFrom: pathname })

/**
 * Props for a router link back to the parent page at `to`, plus whether the
 * page was opened from that parent (for a non-link control that goes back
 * the same way).
 */
const useBackLink = (
  to: string
): { to: string; openedFromTarget: boolean; onClick: (event: MouseEvent<HTMLElement>) => void } => {
  const location = useLocation()
  const navigate = useNavigate()
  const openedFromTarget = (location.state as OpenedFromState | null)?.openedFrom === to

  return {
    to,
    openedFromTarget,
    onClick: (event) => {
      // Modified clicks (new tab / window) keep the link's own behavior
      const plainClick = event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey
      if (openedFromTarget && plainClick) {
        event.preventDefault()
        navigate(-1)
      }
    },
  }
}

export { openedFromState }
export default useBackLink
